import {
  HOSTING_TYPES,
  HostingType,
} from '../../network/httpClients/RepositoryClient/api.schema';
import { QUERY_PARAMS } from '../../routes/consts';
import { getDeploymentConfig, isMtWizardEnabled } from '../../services/config';
import { getBaseDomain, getDynamicSetting } from '../../utils/dynamicSettings';
import { isValidGithubUrl } from '../../utils/url';
import { getSteps } from './configuration';
import {
  LOOPBACK_HOSTNAMES,
  MT_PR_AGENT_WEBHOOK_HANDLER_FOR_GITHUB,
  STEP_IDS,
} from './consts';
import { OAuthState, SelfHostedValidationResult } from './types';

/**
 * Builds the Qodo Merge webhook URL based on tenancy.
 *
 * For ST domains, derives the webhook URL from the current browser hostname so
 * each tenant's manifest carries its own Qodo Merge subdomain.
 *
 * For MT domains, derives the webhook URL from the configured backend
 * (`server_url`). The backend URL is the reliable source on MT because the
 * frontend and backend may sit on different base domains.
 *
 * Returns undefined on localhost so that the caller can substitute a test URL
 * (GitHub rejects localhost webhook URLs).
 *
 * @returns The webhook URL string, or undefined on localhost
 *
 * @example
 * // Running on ST domain: app.merge-pentest-1.st.codium.ai
 * getQodoMergeWebhookUrl();
 * // Returns: 'https://qodo-merge.merge-pentest-1.st.codium.ai/api/v1/github_webhooks'
 *
 * @example
 * // Running on MT production (server_url = https://qodo-platform.qodo.ai)
 * getQodoMergeWebhookUrl();
 * // Returns: 'https://qodo-merge.qodo.ai/api/v1/github_webhooks'
 */
const buildQodoMergeWebhookUrlFromBaseDomain = (baseDomain: string): string =>
  `https://qodo-merge.${baseDomain}/api/v1/github_webhooks`;

const getQodoMergeWebhookUrl = (): string | undefined => {
  const isWizardUnrestricted = !isMtWizardEnabled(getDeploymentConfig());

  if (isWizardUnrestricted) {
    const baseDomain = getBaseDomain(window.location.hostname);

    return buildQodoMergeWebhookUrlFromBaseDomain(baseDomain);
  }

  return MT_PR_AGENT_WEBHOOK_HANDLER_FOR_GITHUB;
};

/**
 * Generates OAuth state object for GitHub manifest flow.
 * The state includes a random secret, workspace_id, and browser_url for validation.
 * This prevents CSRF attacks and preserves workspace context through the OAuth flow.
 *
 * `frontend_redirect_url_params` carries query params that the backend will append
 * to the frontend redirect URL after installation completes. This is used to
 * preserve FE-only context (e.g. `hostingType`) that would otherwise be lost
 * during the GitHub round-trip. The `hostingType` is read internally from the
 * current URL's query string.
 *
 * @param workspaceId - The current workspace ID from organization context
 * @param organizationUrl - The GitHub organization URL
 * @returns JSON stringified state object
 */
export const buildManifestState = (
  workspaceId: string,
  organizationUrl: string,
): string => {
  const url = new URL(organizationUrl);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const hostingTypeParam = urlSearchParams.get(QUERY_PARAMS.HOSTING_TYPE);
  const hostingType: HostingType = isValidHostingType(hostingTypeParam)
    ? hostingTypeParam
    : HOSTING_TYPES.SELF_HOSTED;

  const isGithubServerFlow = hostingType === HOSTING_TYPES.SELF_HOSTED;

  const stateObject: OAuthState & { frontend_base_url: string } = {
    secret: crypto.randomUUID(),
    workspace_id: workspaceId,
    frontend_base_url: window.location.origin,
    ...(isGithubServerFlow && { github_hostname: url.hostname }),
    frontend_redirect_url_params: {
      [QUERY_PARAMS.HOSTING_TYPE]: hostingType,
    },
  };

  return JSON.stringify(stateObject);
};

/**
 * Builds the GitHub App manifest JSON for installation.
 * The manifest defines app permissions, webhook URLs, and callback URLs.
 *
 * Webhook URL resolution:
 * 1. If `fetchedWebhookUrl` is a non-empty string (returned by the BE), use it.
 * 2. Otherwise (BE returned null, fetch errored, or call hasn't happened yet),
 *    fall back to the legacy FE-derived template (`getQodoMergeWebhookUrl`).
 * 3. On localhost, substitute a test URL since GitHub rejects localhost webhooks.
 *
 * @param orgName - GitHub organization name
 * @param fetchedWebhookUrl - Webhook URL returned by the BE (string | null | undefined)
 * @returns Stringified manifest JSON ready for form submission
 */
export const buildManifestJson = (
  orgName: string,
  fetchedWebhookUrl?: string | null,
): string => {
  const serverUrl = getDynamicSetting('server_url');
  const webhookUrl = fetchedWebhookUrl || getQodoMergeWebhookUrl();

  const manifest: Record<string, any> = {
    name: `Qodo for ${orgName}`,
    url: 'https://qodo.ai',
    description: 'Qodo is an AI Code Review platform',
    public: false,
    default_permissions: {
      actions: 'read',
      checks: 'read',
      contents: 'write',
      discussions: 'write',
      issues: 'write',
      pull_requests: 'write',
      metadata: 'read',
    },
    default_events: [
      'check_run',
      'discussion_comment',
      'issue_comment',
      'merge_queue_entry',
      'pull_request',
      'pull_request_review_comment',
      'push',
    ],
    redirect_url: `${serverUrl}/git_integration/v1/manifest/complete`,
    setup_url: `${serverUrl}/git_integration/v1/installation/complete`,
  };

  // Only include webhook URL for non-MT environments.
  // On localhost the webhook URL is omitted — GitHub won't accept localhost URLs
  // and will prompt to configure a webhook after app creation.
  if (webhookUrl) {
    manifest.hook_attributes = {
      url: webhookUrl,
      active: true,
    };
  }

  return JSON.stringify(manifest, null, 2);
};

/**
 * Creates a hidden form with manifest data and submits it to GitHub.
 * This triggers GitHub's app creation flow with the pre-configured manifest.
 * The form submission causes a full page navigation (leaves the SPA).
 *
 * @param manifest - Stringified manifest JSON
 * @param state - State parameter for preserving context through OAuth flow
 * @param orgData
 */
export const createAndSubmitManifestForm = (
  manifest: string,
  state: string,
  orgData: { orgName: string; organizationUrl: string },
): void => {
  const { organizationUrl, orgName } = orgData;
  const githubBaseUrl = new URL(organizationUrl).origin;

  // Create hidden form element
  const form = document.createElement('form');

  form.method = 'POST';
  form.action = `${githubBaseUrl}/organizations/${orgName}/settings/apps/new`;
  form.style.display = 'none';

  // Add manifest field
  const manifestInput = document.createElement('input');

  manifestInput.type = 'hidden';
  manifestInput.name = 'manifest';
  manifestInput.value = manifest;
  form.appendChild(manifestInput);

  // Add state field
  const stateInput = document.createElement('input');

  stateInput.type = 'hidden';
  stateInput.name = 'state';
  stateInput.value = state;
  form.appendChild(stateInput);

  // Append to body, submit, and clean up
  document.body.appendChild(form);
  form.submit();
  // Note: form is not removed as page will navigate away
};

/**
 * Extracts the organization name from a GitHub organization URL.
 *
 * @param url - A parsed URL object
 * @returns The organization name (first path segment), or an empty string if not found
 *
 * @example
 * ```ts
 * const url = new URL('https://github.com/acme-corp/repo');
 * getOrgNameFromUrl(url); // Returns: 'acme-corp'
 * ```
 */
export const getOrgNameFromUrl = (url: URL) =>
  url.pathname.replace(/^\//, '').split('/')[0];

/**
 * Constructs a GitHub organization URL from an organization name.
 *
 * @param orgName - The GitHub organization name (e.g., "acme-corp")
 * @param githubHostname
 * @returns The full GitHub organization URL (e.g., "https://github.com/acme-corp"),
 *          or an empty string if orgName is empty
 *
 * @example
 * ```ts
 * getOrganizationUrlFromOrgName('acme-corp');
 * // Returns: 'https://github.com/acme-corp'
 * ```
 */
export const getOrganizationUrlFromOrgName = (
  orgName: string,
  githubHostname?: string,
) => {
  if (!orgName) return '';

  const baseUrl = githubHostname
    ? `https://${githubHostname}`
    : 'https://github.com';

  return `${baseUrl}/${orgName}`;
};

/**
 * Converts a GitHub organization URL and App slug to its app settings page URL.
 *
 * @param organizationUrl - The GitHub organization URL (e.g., "https://github.com/my-org")
 * @param slug - The GitHub App slug
 * @returns The app settings URL (e.g., "https://github.com/organizations/my-org/settings/apps"),
 *          or an empty string if the input is invalid
 *
 * @example
 * ```ts
 * getAppSettingsUrlFromOrganizationUrl('https://github.com/acme-corp');
 * // Returns: 'https://github.com/organizations/acme-corp/settings/apps'
 * ```
 */
export const getAppSettingsUrlFromOrganizationUrlAndSlug = (
  organizationUrl: string,
  slug: string,
) => {
  if (!organizationUrl) return '';

  try {
    const url = new URL(organizationUrl);
    const orgName = getOrgNameFromUrl(url);

    return `${url.origin}/organizations/${orgName}/settings/apps/${slug}`;
  } catch (error) {
    console.warn('Unable to get app settings url from organization url', error);

    return '';
  }
};

/**
 * Converts a GitHub organization URL to its installations page URL.
 *
 * When `installationId` is provided, returns the URL of the specific installation.
 * When omitted, returns the base installations listing URL for the org. This
 * keeps the link useful during partial / failed loads where the installation id
 * may not be available yet.
 *
 * @param organizationUrl - The GitHub organization URL (e.g., "https://github.com/my-org")
 * @param installationId - The ID of the GitHub App installation (optional)
 * @returns The installations URL, or an empty string if the input is invalid
 *
 * @example
 * ```ts
 * getInstallationsUrlFromOrganizationUrl('https://github.com/acme-corp', 42);
 * // Returns: 'https://github.com/organizations/acme-corp/settings/installations/42'
 *
 * getInstallationsUrlFromOrganizationUrl('https://github.com/acme-corp');
 * // Returns: 'https://github.com/organizations/acme-corp/settings/installations'
 * ```
 */
export const getInstallationsUrlFromOrganizationUrl = (
  organizationUrl: string,
  installationId?: number,
) => {
  if (!organizationUrl) return '';

  try {
    const url = new URL(organizationUrl);
    const orgName = getOrgNameFromUrl(url);
    const baseInstallationsUrl = `${url.origin}/organizations/${orgName}/settings/installations`;

    return installationId !== undefined
      ? `${baseInstallationsUrl}/${installationId}`
      : baseInstallationsUrl;
  } catch (error) {
    console.warn(
      'Unable to get installations url from organization url',
      error,
    );

    return '';
  }
};

export const prependHttpsIfNeeded = (url: string) =>
  url.startsWith('https://') || url.startsWith('http://')
    ? url
    : `https://${url}`;

/**
 * Type guard that checks whether a raw string value is a supported hosting
 * type (`saas` or `self_hosted`). Used to validate the `hostingType` query
 * param at the wizard entry before passing it to validation/placeholder logic.
 *
 * @param value - The candidate value (typically a query param string)
 * @returns true if the value is a valid HostingType
 */
export const isValidHostingType = (
  value: string | null,
): value is HostingType =>
  value === HOSTING_TYPES.SAAS || value === HOSTING_TYPES.SELF_HOSTED;

/**
 * Checks whether a hostname is the GitHub SaaS host (`github.com`).
 *
 * @param hostname - The URL hostname to validate
 * @returns true if the hostname is `github.com`
 */
export const isSaasHostname = (hostname: string): boolean =>
  hostname === 'github.com';

/**
 * Validates if the provided URL is a valid GitHub organization URL.
 * Accepts both org root URLs (e.g. github.com/my-org) and deeper paths (e.g. github.com/my-org/my-repo).
 *
 * @param url - The URL object to validate
 * @returns true if the URL is a valid GitHub org URL with at least an org name in the path
 */
export const isGithubOrgRootUrl = (url: URL): boolean => {
  if (!isValidGithubUrl(url)) {
    return false;
  }

  return url.pathname.split('/').filter(Boolean).length >= 1;
};

/**
 * Checks whether a hostname is a loopback address. Used to block SSRF attempts
 * to local services from a self-hosted GitHub Enterprise URL.
 *
 * @param hostname - The URL hostname to test (already lowercased by URL parser)
 * @returns true for `localhost`, `127.0.0.1`, `0.0.0.0`, or IPv6 loopback
 */
const isLoopbackHost = (hostname: string): boolean =>
  LOOPBACK_HOSTNAMES.has(hostname);

/**
 * Validates whether a URL is acceptable as a self-hosted GitHub Enterprise
 * organization URL.
 *
 * Rules (evaluated in order, first failure wins):
 * 1. Protocol must be `https:` (HTTP rejected for security).
 * 2. Hostname must not be a loopback address (`localhost`, `127.0.0.1`, etc.).
 * 3. Hostname must not be `github.com` (that is the SaaS host).
 * 4. Hostname must not be the bare `ghe.com` (data residency requires a
 *    subdomain like `acme-corp.ghe.com`). Subdomains of `ghe.com` are allowed.
 * 5. Path must contain exactly one non-empty segment — the organization name.
 *    Deeper paths (e.g. `/org/repo/settings`) are rejected.
 *
 * Any other hostname is accepted, including custom subdomains
 * (`git.internal.company.com`), Enterprise vanity hosts (`github.acme.corp`),
 * GHE Cloud data residency hosts (`acme-corp.ghe.com`), hostnames with custom
 * ports (`github.local:8443`), and raw IPv4 addresses (`10.0.50.15`).
 *
 * @param url - The parsed URL to validate
 * @returns A discriminated result; on rejection, `reason` identifies which rule failed
 */
export const isValidSelfHostedGithubUrl = (
  url: URL,
): SelfHostedValidationResult => {
  if (url.protocol !== 'https:') {
    return { ok: false, reason: 'insecure-protocol' };
  }

  if (isLoopbackHost(url.hostname)) {
    return { ok: false, reason: 'localhost-not-allowed' };
  }

  if (url.hostname === 'github.com') {
    return { ok: false, reason: 'saas-host-on-self-hosted' };
  }

  if (url.hostname === 'ghe.com') {
    return { ok: false, reason: 'ghe-requires-subdomain' };
  }

  const segments = url.pathname.split('/').filter(Boolean);

  if (segments.length !== 1) {
    return { ok: false, reason: 'path-must-be-org-only' };
  }

  return { ok: true };
};

/**
 * Extracts the step index from the GitHub installation URL path.
 * Converts URL step number (1-based) to array index (0-based).
 *
 * @param pathname - The URL pathname (e.g., '/integrations/github/3')
 * @returns The step index (0-based), or undefined if not a valid GitHub installation path
 *
 * @example
 * ```ts
 * getStepIndexFromPath('/integrations/github/3'); // Returns: 2 (step 3 → index 2)
 * getStepIndexFromPath('/integrations/github/1'); // Returns: 0 (step 1 → index 0)
 * getStepIndexFromPath('/integrations/other'); // Returns: undefined
 * ```
 */
export const getStepIndexFromPath = (pathname: string): number | undefined => {
  const match = pathname.match(/\/integrations\/github\/(\d+)/);

  if (!match) return undefined;

  const stepNumber = parseInt(match[1], 10);

  return stepNumber - 1; // Convert to 0-based index
};

export const getManifestJson = (
  organizationUrl: string,
  fetchedWebhookUrl?: string | null,
) => {
  if (!organizationUrl) {
    return undefined;
  }

  try {
    const url = new URL(organizationUrl);
    const orgName = getOrgNameFromUrl(url);

    return buildManifestJson(orgName, fetchedWebhookUrl);
  } catch (error) {
    console.error('Failed to generate manifest:', error);

    return undefined;
  }
};

/**
 * Validates if the state from URL matches the stored state.
 * Used to verify that users returning from GitHub's app installation flow
 * are legitimate and not attempting to bypass the wizard steps.
 *
 * Flow:
 * 1. User submits manifest on step 2 with state: {"secret":"<uuid>","workspace_id":"<id>"}
 * 2. State is stored in localStorage
 * 3. Backend redirects back with ?state={"secret":"<uuid>","workspace_id":"<id>"}
 * 4. This function validates both secret and workspace_id match
 *
 * @param storedState - The stored state object or string from localStorage
 * @returns true if URL state matches stored state
 */
export const isStateValid = (
  storedState: OAuthState | string | undefined,
): boolean => {
  if (!storedState) {
    return false;
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlStateString = urlSearchParams.get('state');

  if (!urlStateString) {
    return false;
  }

  try {
    // Parse URL state (always a string from query params)
    const urlState: OAuthState = JSON.parse(urlStateString);

    // Handle stored state - could be object (from useLocalStorage) or string
    const parsedStoredState: OAuthState =
      typeof storedState === 'string' ? JSON.parse(storedState) : storedState;

    // Validate both secret and workspace_id match
    const isSecretValid = urlState.secret === parsedStoredState.secret;
    const isWorkspaceIdValid =
      urlState.workspace_id === parsedStoredState.workspace_id;

    return isSecretValid && isWorkspaceIdValid;
  } catch (error) {
    // If parsing fails, state is invalid
    console.error('Failed to parse OAuth state:', error);

    return false;
  }
};

export const getCheckConnectionStepIndex = () =>
  getSteps().findIndex((step) => step.id === STEP_IDS.CHECK_CONNECTION);

/**
 * Extracts the GitHub Enterprise hostname from the stored OAuth state.
 * Returns undefined for GitHub Cloud installations (no hostname stored).
 *
 * @param storedState - The stored state from localStorage
 * @returns The GitHub Enterprise hostname, or undefined if Cloud
 */
export const getGithubHostnameFromState = (
  storedState: OAuthState | string | undefined,
): string | undefined => {
  if (!storedState) return undefined;

  try {
    const parsed: OAuthState =
      typeof storedState === 'string' ? JSON.parse(storedState) : storedState;

    return parsed.github_hostname;
  } catch {
    return undefined;
  }
};
